/**
 * Tokens for staggered word spans.
 *
 * Spaced copy keeps punctuation on the word ("benchmarks,", "disease-centric"),
 * matching the hand-written spans these animations used to render. GSAP and
 * the section CSS only target `span`, so a comma or hyphen left as a raw text
 * node stays visible and drops the word color.
 *
 * Unspaced copy (ja, zh, …) uses Intl.Segmenter in the page locale. Punctuation
 * is still a span: Segmenter reports "。" as not word-like, and skipping it
 * leaves the mark on screen while the words animate.
 */
export function splitForStagger(
  text: string,
  locale?: string,
): { text: string; isWord: boolean }[] {
  if (/\s/.test(text)) {
    return text
      .split(/(\s+)/)
      .filter(Boolean)
      .map((part) => ({
        text: part,
        isWord: !/^\s+$/.test(part),
      }));
  }

  if (typeof Intl !== 'undefined' && 'Segmenter' in Intl) {
    try {
      const segmenter = new Intl.Segmenter(locale, { granularity: 'word' });
      return [...segmenter.segment(text)]
        .filter(({ segment }) => segment.length > 0)
        .map(({ segment }) => ({
          text: segment,
          isWord: !/^\s+$/.test(segment),
        }));
    } catch {
      // fall through
    }
  }

  return text ? [{ text, isWord: true }] : [];
}

/**
 * User-perceived characters for per-letter spans. `split('')` cuts Thai vowel
 * and tone marks off their consonant, and each lands in its own span.
 */
export function splitGraphemes(text: string): string[] {
  if (typeof Intl !== 'undefined' && 'Segmenter' in Intl) {
    const segmenter = new Intl.Segmenter(undefined, {
      granularity: 'grapheme',
    });
    return [...segmenter.segment(text)].map(({ segment }) => segment);
  }

  return Array.from(text);
}
